import { useState } from "react";
import { controlToastState, Modal, TextInput } from "risa-oasis-ui_v2";
import {
  PRIMARY_CARE_OUTCOME_OPTIONS,
  PRIMARY_CARE_POC_OPTIONS,
  PRIMARY_CARE_ROUTE_VIA_OPTIONS,
} from "../../../constants/primaryCareSubmissionOptions";
import { ModalId } from "../../../enums/modalId";
import BadgeDropdown from "../../badgeDropdown/badgeDropdown";
import LabelValueRow from "../../labelValueRow/labelValueRow";
import { onAsteraPrimaryCareSave } from "./actionHandlers/onAsteraPrimaryCareSave";
import {
  AsteraPrimaryCareSubmissionDataKeys,
  useAsteraPrimaryCareSubmissionData,
} from "./hooks/useAsteraPrimaryCareSubmissionData";

interface AsteraPrimaryCareSubmissionProps {
  onClose: () => void;
  onSuccess?: () => void;
  metaData: {
    data: any;
  };
}

const AsteraPrimaryCareSubmission = (
  props: AsteraPrimaryCareSubmissionProps,
) => {
  const { data } = props.metaData;

  const {
    submissionData,
    handleDataChange,
    handleOutcomeChange,
    handleRouteViaChange,
    isFormValid,
  } = useAsteraPrimaryCareSubmissionData(
    data ? (data as Record<string, string>) : undefined,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSave = async () => {
    setIsLoading(true);
    await onAsteraPrimaryCareSave({
      data,
      outcome: submissionData.outcome,
      routeVia: submissionData.routeVia,
      poc: submissionData.poc,
      key: submissionData.key,
      comments: submissionData.comments,
      onSuccess: () => {
        controlToastState("astera-primary-care-submission-save-success");
        props.onSuccess?.();
        props.onClose();
      },
      onError: (error) => {
        console.error("Error saving Astera Primary Care submission:", error);
        controlToastState("astera-primary-care-submission-save-error");
      },
    });
    setIsLoading(false);
  };

  return (
    <Modal
      dialogId={ModalId.ASTERA_PRIMARY_CARE_SUBMISSION_MODAL}
      title="Submission Summary"
      onClose={props.onClose}
      onCancel={props.onClose}
      onSave={handleSave}
      saveButtonText={isLoading ? "Submitting..." : "Submit"}
      cancelText="Cancel"
      disableSave={!isFormValid || isLoading}
    >
      <div className="flex flex-col gap-1">
        <div className="flex flex-col gap-2">
          <LabelValueRow label="Task:">
            <div className="text-sm font-normal text-primaryGray-2">
              {data?.case_name || data?.task || ""}
            </div>
          </LabelValueRow>

          <LabelValueRow label="MRN:">
            <div className="text-sm font-normal text-primaryGray-2">
              {data?.patient_mrn || data?.mrn || ""}
            </div>
          </LabelValueRow>

          <LabelValueRow label="Key:">
            <TextInput
              id="key-input"
              label="Key"
              hideLabel={true}
              defaultValue={submissionData.key}
              onChange={(e) =>
                handleDataChange(
                  AsteraPrimaryCareSubmissionDataKeys.KEY,
                  e.value,
                )
              }
            />
          </LabelValueRow>
        </div>

        <div className="flex flex-col justify-center gap-2">
          <LabelValueRow label="Outcome:">
            <BadgeDropdown
              badgeList={PRIMARY_CARE_OUTCOME_OPTIONS}
              defaultBadge={
                PRIMARY_CARE_OUTCOME_OPTIONS.find(
                  (b) => b.id === submissionData.outcome,
                ) ?? null
              }
              showBorder={true}
              borderColor="#A8A8A8"
              onClick={(id: string) => {
                handleOutcomeChange(id);
              }}
            />
          </LabelValueRow>

          <LabelValueRow label="Route Via:">
            <BadgeDropdown
              badgeList={PRIMARY_CARE_ROUTE_VIA_OPTIONS}
              defaultBadge={
                PRIMARY_CARE_ROUTE_VIA_OPTIONS.find(
                  (b) => b.id === submissionData.routeVia,
                ) ?? null
              }
              showBorder={true}
              borderColor="#A8A8A8"
              onClick={(id: string) => {
                handleRouteViaChange(id);
              }}
            />
          </LabelValueRow>

          <LabelValueRow label="POC:" showDivider={false}>
            <BadgeDropdown
              badgeList={PRIMARY_CARE_POC_OPTIONS}
              defaultBadge={
                PRIMARY_CARE_POC_OPTIONS.find(
                  (b) => b.id === submissionData.poc,
                ) ?? null
              }
              showBorder={true}
              borderColor="#A8A8A8"
              onClick={(id: string) => {
                handleDataChange(AsteraPrimaryCareSubmissionDataKeys.POC, id);
              }}
            />
          </LabelValueRow>
        </div>

        <div className="my-2">
          <TextInput
            label="Comments"
            placeholder="Add your comments here"
            id="primary-care-submission-comments-input"
            defaultValue={submissionData.comments}
            onChange={(e) =>
              handleDataChange(
                AsteraPrimaryCareSubmissionDataKeys.COMMENTS,
                e.value,
              )
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default AsteraPrimaryCareSubmission;
